import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { vi } from 'date-fns/locale';
import LabeledField from '@/__core/components/LabeledField';
import {
  readUiState,
  UiStateStorageConfig,
  writeUiState,
} from '@/modules/commons/config/uiStateStorage';

const periodPickerTextFieldSx = {
  width: { xs: 150, sm: 150 },
  minWidth: 150,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0.5,
    height: '30px',
    fontSize: '0.875rem',
    '& fieldset': {
      borderColor: '#d1d5db',
    },
    '&:hover fieldset': {
      borderColor: '#9ca3af',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
    },
  },
  '& .MuiInputBase-input': {
    padding: '6px 12px',
  },
};

interface MonthPeriodPickerProps {
  selectedPeriod?: Date | null;
  onPeriodChange?: (value: Date | null) => void;
  label?: string;
  labelWidth?: number | string;
  disabled?: boolean;
  persistConfig?: UiStateStorageConfig;
  restoreWhen?: boolean;
}

export default function MonthPeriodPicker({
  selectedPeriod = new Date(),
  onPeriodChange,
  label = 'Tháng',
  labelWidth = 50,
  disabled = false,
  persistConfig,
  restoreWhen = true,
}: MonthPeriodPickerProps) {
  const restoredRef = React.useRef(false);

  React.useEffect(() => {
    if (!restoreWhen) {
      restoredRef.current = false;
      return;
    }

    if (!persistConfig || !onPeriodChange || restoredRef.current) {
      return;
    }

    const restoredIso = readUiState<string | null>(persistConfig, null);
    restoredRef.current = true;

    if (!restoredIso) {
      return;
    }

    const restoredDate = new Date(restoredIso);
    if (Number.isNaN(restoredDate.getTime())) {
      return;
    }

    onPeriodChange(restoredDate);
  }, [onPeriodChange, persistConfig, restoreWhen]);

  const handleChange = (value: Date | null) => {
    onPeriodChange?.(value);

    if (!persistConfig) {
      return;
    }

    writeUiState<string | null>(
      persistConfig,
      value && !Number.isNaN(value.getTime()) ? value.toISOString() : null
    );
  };

  return (
    <LabeledField label={label} labelWidth={labelWidth}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
        <DatePicker
          views={['year', 'month']}
          openTo='month'
          disabled={disabled}
          value={selectedPeriod}
          onChange={handleChange}
          format='MM/yyyy'
          disableFuture
          maxDate={new Date()}
          slotProps={{
            textField: {
              size: 'small',
              disabled,
              sx: periodPickerTextFieldSx,
            },
          }}
        />
      </LocalizationProvider>
    </LabeledField>
  );
}
